import React, { useMemo } from 'react';
import styled from 'styled-components';
import { colors } from 'styleguide';

import { Tick, Dash } from 'craft/icons';

export interface WrapperProps {
  isActive: boolean;
  disabled?: boolean;
  responsiveWidth?: boolean;
}

const Wrapper = styled.div<WrapperProps>`
  background: white;
  height: 32px;
  ${(props) => !props.responsiveWidth && 'width: 32px'};
  cursor: pointer;
  box-sizing: border-box;

  display: flex;
  justify-content: center;
  align-items: center;

  border-left: 1px solid ${colors('neutral', 3)};
  border-top: 1px solid ${colors('neutral', 3)};
  border-bottom: 1px solid ${colors('neutral', 3)};

  transition: background 0.4s ease;

  ${(props: { disabled?: boolean }) => (props.disabled ? 'cursor: disabled;' : '')}

  ${(props: WrapperProps) => {
    return (
      props.isActive &&
      `
        background: ${colors('primary')};
      `
    );
  }}

  & > svg > *, & > span {
    transition: all 0.4s ease;
  }

  & > svg {
    stroke: ${(props: WrapperProps) => (props.isActive ? 'white' : colors('neutral', 3))};
    fill: ${(props: WrapperProps) => (props.isActive ? 'white' : colors('neutral', 3))};
  }

  & > span {
    color: ${(props: WrapperProps) => (props.isActive ? 'white' : colors('neutral', 3))};
  }

  &:hover > svg {
    stroke: ${(props: WrapperProps) => (props.isActive ? 'white' : colors('neutral', 2))};
    fill: ${(props: WrapperProps) => (props.isActive ? 'white' : colors('neutral', 2))};
  }

  &:hover > span {
    color: ${(props: WrapperProps) => (props.isActive ? 'white' : colors('neutral', 2))};
  }
`;

interface Props {
  isActive: boolean;
  icon?: any;
  id?: string;
  toggleGroup?: string;
  onClick?: () => boolean | void;
  disabled?: boolean;
  'data-cy'?: string;
  responsiveWidth?: boolean;
}

const Toggle = React.forwardRef(
  (
    { isActive, icon, onClick, id, toggleGroup, disabled, 'data-cy': dataCy, ...otherProps }: Props,
    ref: any,
  ) => {
    let iconComponent = icon;
    if (!iconComponent) {
      iconComponent = isActive ? Tick : Dash;
    }

    const handleClick = () => {
      if (disabled) {
        return;
      }

      if (onClick) {
        onClick();
      }
    };

    const memoizedIconComp = useMemo(() => iconComponent, [isActive]);

    return (
      <Wrapper
        id={id}
        disabled={disabled}
        onClick={onClick && handleClick}
        isActive={isActive}
        className={isActive ? 'selected' : undefined}
        ref={ref}
        data-cy={dataCy}
        {...otherProps}
      >
        {React.createElement(memoizedIconComp, { color: 'inherit' })}
      </Wrapper>
    );
  },
);

export default Toggle;
